import { Injectable } from '@angular/core';

import { Observable, isObservable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    InterpolatableTranslation,
    InterpolationParameters,
    Translation,
    TranslateService,
    TranslationObject,
} from '@ngx-translate/core';

import { resolveTranslationKey } from './translation-key.utils';

/**
 * Drop-in replacement for ngx-translate's `TranslateService` that is tolerant to
 * inconsistent key styles. Translation keys (and the keys inside the loaded
 * dictionaries) are matched in a style-agnostic way, so `event-type`,
 * `event_type` and `eventType` all resolve to the same translation - whether the
 * mismatch is in the key passed by the caller or in the translation file itself.
 *
 * Every lookup - `get`, `instant`, `stream`, the `translate` pipe and the
 * `translate` directive - funnels through `getParsedResult`, so overriding this
 * single method makes the whole translation layer style-agnostic without
 * touching any call site.
 *
 * The loaded dictionaries are never mutated: keys are only corrected for the
 * lookup, which keeps object-shaped translations (e.g. the PrimeNG config under
 * `common.primeng`, read by exact property name) intact.
 *
 * NOTE: no constructor is declared on purpose - Angular's Ivy inherits the base
 * `TranslateService` injection metadata, so DI keeps working unchanged.
 */
@Injectable()
export class NormalizingTranslateService extends TranslateService {
    public override getParsedResult(
        translations: InterpolatableTranslation,
        key: string | string[],
        interpolateParams?: InterpolationParameters
    ):
        | Translation
        | TranslationObject
        | Observable<Translation | TranslationObject> {
        if (Array.isArray(key)) {
            const resolvedKeys = key.map(originalKey =>
                this.resolveKey(translations, originalKey)
            );
            const translated = super.getParsedResult(
                translations,
                resolvedKeys,
                interpolateParams
            );

            // Restore the caller's original keys on the returned result object.
            return isObservable(translated)
                ? this.mapResult(translated, key, resolvedKeys)
                : this.restoreKeys(translated, key, resolvedKeys);
        }

        const resolvedKey = this.resolveKey(translations, key);
        return super.getParsedResult(
            translations,
            resolvedKey,
            interpolateParams
        );
    }

    /**
     * Corrects a single key against the current language dictionary, falling back
     * to the default language dictionary (where ngx-translate looks when a key is
     * missing from the active language).
     */
    private resolveKey(
        translations: InterpolatableTranslation,
        key: string
    ): string {
        if (typeof key !== 'string') {
            return key;
        }

        const resolved = resolveTranslationKey(translations, key);
        if (resolved !== key) {
            return resolved;
        }

        const defaultLang = this.defaultLang;
        if (defaultLang && defaultLang !== this.currentLang) {
            return resolveTranslationKey(this.translations?.[defaultLang], key);
        }

        return key;
    }

    private mapResult(
        result: Observable<Translation | TranslationObject>,
        originalKeys: string[],
        resolvedKeys: string[]
    ): Observable<Translation | TranslationObject> {
        return result.pipe(
            map(value => this.restoreKeys(value, originalKeys, resolvedKeys))
        );
    }

    private restoreKeys(
        value: Translation | TranslationObject,
        originalKeys: string[],
        resolvedKeys: string[]
    ): Translation | TranslationObject {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return value;
        }

        const source = value as Record<string, Translation>;
        const restored: Record<string, Translation> = {};
        originalKeys.forEach((originalKey, index) => {
            restored[originalKey] = source[resolvedKeys[index]];
        });
        return restored;
    }
}
